import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import FileUpload from '../components/FileUpload';
import FaqAccordion from '../components/FaqAccordion';
import ShareButtons from '../components/ShareButtons';
import { trackEvent } from '../utils/analytics';
import { Download, ArrowRight, RotateCcw, Wand2, Check, XCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { downloadFile } from '../utils/download';

export default function RemoveBackground() {
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleRemove = async () => {
    if (!files.length) return toast.error('Please select an image');
    trackEvent('conversion_started', { tool_name: 'Remove Background' });
    setStatus('processing'); setError(''); setResult(null);

    const formData = new FormData();
    formData.append('file', files[0]);

    try {
      const res = await axios.post('/api/convert/remove-bg', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setStatus('done');
      setResult(res.data);
      toast.success('Background removed!');
    } catch (err) {
      setStatus('error');
      setError((err.response && err.response.data && err.response.data.error) || 'Background removal failed');
    }
  };

  const handleReset = () => { setFiles([]); setStatus(null); setResult(null); setError(''); };

  return (
    <div>
      <Helmet>
        <title>Remove Background from Image Free — AI Background Remover | ILoveConvert</title>
        <meta name="description" content="Free AI background remover — upload a JPG, PNG, WebP, GIF, TIFF or AVIF and get a transparent PNG cutout in seconds. No signup, no watermark." />
        <meta name="keywords" content="remove background from image, background remover, remove bg, AI background removal, transparent PNG, free background eraser, online background remover, photo background remover" />
        <link rel="canonical" href="https://iloveconvert.in/remove-background" />
        <meta property="og:title" content="Remove Background from Image Free — AI Background Remover | ILoveConvert" />
        <meta property="og:description" content="Upload any photo and get an instant transparent PNG cutout with AI. Free, no signup, no watermark." />
        <meta property="og:url" content="https://iloveconvert.in/remove-background" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="Remove Background from Image Free — AI Background Remover" />
        <meta name="twitter:description" content="Free AI background remover. Upload a photo, download a transparent PNG. No signup needed." />
      </Helmet>

      <section className="hero" style={{ padding: '40px 20px' }}>
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}><Wand2 size={44} strokeWidth={1.5} /></div>
        <h1 style={{ fontSize: 'clamp(1.6rem,4vw,2.4rem)', marginBottom: 8 }}>Remove Background</h1>
        <p>Need to remove a background from a photo? Just upload your image and let the tool do the work for you.</p>
      </section>

      <div style={{ maxWidth: 900, margin: '20px auto', padding: '0 20px' }}>
        <p style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--text-muted)' }}>
          Our AI background remover automatically finds the main subject and removes the background, leaving you with a
          clean, transparent PNG. You can use it for people, products, animals, or other clear objects.
        </p>
        <p style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--text-muted)' }}>
          It supports JPG, PNG, WebP, GIF, TIFF, and AVIF. After the background is removed, you can preview the image
          and download it.
        </p>
        <p style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--text-muted)' }}>
          No Photoshop, no complicated editing, and no manual cutting. Upload your photo, remove the background,
          and you're done.
        </p>
      </div>

      <div className="upload-page">
        {!status && (
          <>
            <FileUpload onFiles={setFiles} accept={{ 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'], 'image/webp': ['.webp'], 'image/gif': ['.gif'], 'image/tiff': ['.tif', '.tiff'], 'image/avif': ['.avif'] }} maxSize={25} />
            {files.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <button className="btn btn-primary btn-lg" onClick={handleRemove} style={{ width: '100%' }}>
                  <ArrowRight size={18} /> Remove Background
                </button>
              </div>
            )}
          </>
        )}

        {status === 'processing' && (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <div className="spinner" />
            <p style={{ marginTop: 20, fontWeight: 600 }}>Detecting subject and removing background...</p>
            <p style={{ marginTop: 6, fontSize: '0.82rem', color: 'var(--text-muted)' }}>Usually takes a few seconds</p>
          </div>
        )}

        {status === 'done' && result && (
          <>
            <div className="result-box">
              <div className="check"><Check size={32} strokeWidth={3} /></div>
              <h3>Background Removed!</h3>
              <div style={{
                margin: '16px auto', maxWidth: 320, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)',
                backgroundImage: 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%), linear-gradient(-45deg, #e5e7eb 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e7eb 75%), linear-gradient(-45deg, transparent 75%, #e5e7eb 75%)',
                backgroundSize: '20px 20px', backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
              }}>
                <img src={result.downloadUrl} alt="Background removed" style={{ width: '100%', display: 'block' }} />
              </div>
              <button onClick={() => downloadFile(result.downloadUrl, result.downloadUrl.split('/').pop(), 'Remove Background')} className="download-btn">
                <Download size={18} /> Download PNG
              </button>
              <ShareButtons toolLabel="Remove Background" />
            </div>
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <button className="btn btn-outline" onClick={handleReset}><RotateCcw size={16} /> Remove Another</button>
            </div>
          </>
        )}

        {status === 'error' && (
          <div style={{ background: '#fff0f0', border: '1px solid #fca5a5', borderRadius: 'var(--radius)', padding: 28, textAlign: 'center' }}>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center', color: '#dc2626' }}><XCircle size={40} /></div>
            <h3 style={{ color: '#dc2626', fontFamily: 'Sora, sans-serif', marginBottom: 8 }}>Failed</h3>
            <p style={{ color: '#b91c1c', marginBottom: 20 }}>{error}</p>
            <button className="btn btn-primary" onClick={handleReset}><RotateCcw size={16} /> Try Again</button>
          </div>
        )}
      </div>

      <div style={{ maxWidth: 900, margin: '40px auto', padding: '0 20px' }}>
        <h2>How AI Background Removal Works</h2>
        <ul>
          <li><strong>Upload your photo</strong> — People, products, animals, and objects work well.</li>
          <li><strong>AI finds the subject</strong> — It looks at the image and separates the main subject from the background.</li>
          <li><strong>Background is removed</strong> — The background becomes transparent while keeping the subject clear.</li>
          <li><strong>Preview and download</strong> — Check the result, then download your transparent PNG.</li>
        </ul>

        <h2>Where You Can Use It</h2>
        <ul>
          <li>Product photos for online stores</li>
          <li>Profile pictures and headshots</li>
          <li>Posters, thumbnails, and presentations</li>
          <li>Stickers and simple image designs</li>
        </ul>

        <h2>Tips for Better Results</h2>
        <ul>
          <li>Use a photo with good lighting and a clear difference between the subject and background.</li>
          <li>Busy backgrounds or similar colours can make the edges harder to detect.</li>
          <li>The tool works best with photos of people, products, and animals rather than logos or vector graphics.</li>
          <li>If you notice a small issue around the edges, you can edit the PNG later using any image editor.</li>
        </ul>

        <h2>Frequently Asked Questions</h2>
        <FaqAccordion faqs={[
          { q: 'Is this tool free?', a: 'Yes, you can remove backgrounds from your images for free.' },
          { q: 'What image formats are supported?', a: 'You can upload JPG, PNG, WebP, GIF, TIFF, and AVIF images.' },
          { q: 'Does it work with any photo?', a: 'It works best with people, products, animals, and clearly visible objects. Photos with a simple, clear background usually give better results.' },
          { q: 'Are my files secure?', a: 'Yes. Your images are processed securely and are not sent to a third-party AI service.' },
        ]} />
      </div>
    </div>
  );
}
